import { DashboardTabType } from "gql/graphql";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import useLogToMixPanel from "./useLogToMixpanel";

const minimumLoggedTimeMS = 250;

export function useTabTimeTracking() {
	const location = useLocation();
	const startTimeRef = useRef<number>(performance.now());
	const { logToMixpanel } = useLogToMixPanel();

	const tabType = useMemo(
		() =>
			location.pathname.includes("/incident/") ? DashboardTabType.INCIDENT
			: location.pathname.includes("/columns/") ? DashboardTabType.COLUMNS
			: location.pathname.includes("/alerts/") ? DashboardTabType.ALERTS
			: undefined,
		[location.pathname]
	);

	const resumeLoggingTime = useCallback(() => {
		startTimeRef.current = performance.now();
	}, []);

	const pauseLoggingTime = useCallback(() => {
		const endTime = performance.now();
		const totalTime = Math.round(endTime - startTimeRef.current);
		if (tabType && totalTime >= minimumLoggedTimeMS) {
			logToMixpanel("log_time_on_tab", {
				tab_type: tabType,
				duration_seconds: totalTime / 1000,
			});
		}
		startTimeRef.current = performance.now();
	}, [tabType, logToMixpanel]);

	useEffect(() => {
		resumeLoggingTime();

		return pauseLoggingTime;
	}, [tabType, resumeLoggingTime, pauseLoggingTime]);

	useEffect(() => {
		window.addEventListener("focus", resumeLoggingTime);
		window.addEventListener("blur", pauseLoggingTime);

		return () => {
			window.removeEventListener("focus", resumeLoggingTime);
			window.removeEventListener("blur", pauseLoggingTime);
		};
	}, [resumeLoggingTime, pauseLoggingTime]);
}
